import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/styles';
import { v4 as uuid } from 'uuid';
import {
  format as d3Format,
  select as d3Select,
  pack as d3Pack,
  hierarchy as d3Hierarchy
} from 'd3';

type BubbleChartData = {
  label: string;
  value: number;
  id: string;
  color: any;
};

type BubbleChartProps = {
  data: BubbleChartData[];
  width?: number;
  onClick: (target: any, chart: any) => void;
};

export const BubbleChart: React.FC<BubbleChartProps> = ({
  data,
  width = 900,
  onClick
}: BubbleChartProps) => {
  const d3Container = useRef(null);
  const theme = useTheme();
  useEffect(() => {
    if (data && d3Container.current) {
      const format = d3Format(',d');
      const svgCanvas = d3Select(d3Container.current);
      const pack = d3Pack()
        .size([width - 2, width - 2])
        .padding(5);

      const rootObj = d3Hierarchy({ children: data }).sum((d: any) => d.value);
      const rootNodes = pack(rootObj as any).leaves();

      const svg = svgCanvas
        .attr('viewBox', [0, 0, width, width])
        .attr('font-size', String(25))
        .attr('font-family', String(theme.typography.fontFamily))
        .attr('text-anchor', 'middle')
        .attr('height', '300');

      svg.selectAll('g').remove();
      const leaf = svg
        .selectAll('g')
        .data(rootNodes)
        .join('g')
        .attr('transform', (d) => `translate(${d.x + 1},${d.y + 1})`);

      leaf
        .append('circle')
        .attr('id', (d: any) => (d.leafUid = uuid()))
        .style('cursor', 'pointer')
        .attr('r', (d) => d.r)
        .attr('fill-opacity', 0.7)
        .attr('fill', (d: any) => d.data.color)
        .attr('data-testid', (d: any) => `bubble_${d.data.label}`)
        .on('click', onClick);

      leaf
        .append('clipPath')
        .attr('id', (d: any) => (d.clipUid = uuid()))
        .append('use')
        .attr('xlink:href', (d: any) => `#${d.leafUid}`);

      leaf
        .append('text')
        .attr('clip-path', (d: any) => `url(#${d.clipUid})`)
        .style('cursor', 'pointer')
        .on('click', onClick)
        .selectAll('tspan')
        .data((d: any) =>
          [d.value.toFixed(0)].concat(
            (d.data.label || '').split(/(?=[A-Z][a-z])|\s+/g)
          )
        )
        .enter()
        .append('tspan')
        .attr('x', 0)
        .attr('y', (d, i, nodes) => `${i - nodes.length / 2 + 0.8}em`)
        .attr('style', (d, i) => (i === 0 ? 'font-weight:bold;' : ''))
        .text((d) => d);

      leaf
        .append('title')
        .text((d: any) => `${format(d.value)}\n${d.data.label}`);
    }
  }, [
    width,
    data,
    onClick,
    theme.typography.caption.fontSize,
    theme.typography.fontFamily
  ]);

  return <svg ref={d3Container} />;
};
